import type { AppState } from '../state/store';
import { findElementById, setHidden } from './dom';
import { formatMoney, formatPercent } from './format';

const ratingStyles = {
  'very-bad': { bg: 'bg-red-50', text: 'text-red-700' },
  neutral: { bg: 'bg-yellow-50', text: 'text-yellow-700' },
  profitable: { bg: 'bg-green-50', text: 'text-green-700' }
} as const;

const allBg = ['bg-red-50', 'bg-yellow-50', 'bg-green-50'];
const allText = ['text-red-700', 'text-yellow-700', 'text-green-700'];

function getRating(type: 'profit' | 'margin' | 'roi', value: number): keyof typeof ratingStyles {
  if (type === 'profit') return value < 2 ? 'very-bad' : value < 5 ? 'neutral' : 'profitable';
  if (type === 'margin') return value < 10 ? 'very-bad' : value < 20 ? 'neutral' : 'profitable';
  return value < 30 ? 'very-bad' : value < 60 ? 'neutral' : 'profitable';
}

function applyRating(root: ParentNode, type: 'profit' | 'margin' | 'roi', value: number, valueId: string, cardId: string): void {
  const valueEl = findElementById<HTMLElement>(root, valueId);
  const cardEl = findElementById<HTMLElement>(root, cardId);
  if (!valueEl || !cardEl) return;

  const rating = getRating(type, value);
  const style = ratingStyles[rating];
  cardEl.classList.remove(...allBg);
  valueEl.classList.remove(...allText);
  cardEl.classList.add(style.bg);
  valueEl.classList.add(style.text);
}

function setText(root: ParentNode, id: string, value: string): void {
  const node = findElementById<HTMLElement>(root, id);
  if (node) node.textContent = value;
}

function getCurrencySymbol(locale: string, currency: string): string {
  const parts = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).formatToParts(1);
  const currencyPart = parts.find((part) => part.type === 'currency');
  return currencyPart ? currencyPart.value : currency;
}

function formatVerifiedDate(raw: string, locale: string): string {
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(parsed);
}

function updateLandedLabel(root: ParentNode, state: AppState): void {
  const advanced = state.input.inbound + state.input.duties + state.input.prep;
  const label = advanced > 0 ? 'Landed cost per unit' : 'Landed cost per unit (est.)';
  setText(root, 'landed-cost-label-text', label);
}

function syncSliderCurrency(root: ParentNode, locale: string, currency: string): void {
  const slider = findElementById<HTMLInputElement>(root, 'price-slider');
  const sliderValue = findElementById<HTMLElement>(root, 'slider-value');
  if (!slider || !sliderValue || !slider.value) return;
  const value = Number(slider.value);
  if (!Number.isFinite(value)) return;
  sliderValue.textContent = formatMoney(value, locale, currency);
}

export function render(state: AppState, root: ParentNode = document): void {
  const profile = state.profile;
  const locale = profile.locale;
  const currency = profile.currency;

  setText(root, 'price-currency-code', currency);
  syncSliderCurrency(root, locale, currency);
  updateLandedLabel(root, state);

  const verifiedText = `Fee data last verified: ${formatVerifiedDate(profile.last_verified_at, locale)}`;
  setText(root, 'fee-data-verified-note', verifiedText);

  const noteEl = findElementById<HTMLElement>(root, 'fee-breakdown-currency-note');
  if (noteEl) {
    const selectedSymbol = getCurrencySymbol(locale, currency);
    const usdSymbol = getCurrencySymbol('en-US', 'USD');
    const show = state.market !== 'US' && selectedSymbol === usdSymbol;
    noteEl.textContent = show ? `(${currency})` : '';
    setHidden(noteEl, !show);
  }

  if (!state.result) {
    const results = findElementById<HTMLElement>(root, 'results');
    setHidden(results, true);
    setText(root, 'results-live-region', '');
    return;
  }

  const r = state.result;
  setText(root, 'profit-per-unit', formatMoney(r.profitPerUnit, locale, currency));
  setText(root, 'margin', formatPercent(r.marginPct));
  setText(root, 'roi', formatPercent(r.roiPct));

  setText(root, 'landed-cost', formatMoney(r.landedCost, locale, currency));
  setText(root, 'referral-fee', formatMoney(r.referralFee, locale, currency));
  setText(root, 'variable-closing-fee', formatMoney(r.variableClosingFee, locale, currency));
  setText(root, 'fulfillment-fee', formatMoney(r.fulfillmentFee, locale, currency));
  setText(root, 'fulfillment-surcharge-fee', formatMoney(r.fulfillmentSurchargeFee, locale, currency));
  setText(root, 'inbound-placement-fee-value', formatMoney(r.inboundPlacementFee, locale, currency));
  setText(root, 'storage-off', formatMoney(r.storageOffSeason, locale, currency));
  setText(root, 'storage-peak', formatMoney(r.storagePeakSeason, locale, currency));
  setText(root, 'consistent-fees-total', formatMoney(r.consistentFeesTotal, locale, currency));
  setText(root, 'expected-returns-cost', formatMoney(r.expectedReturnsCost, locale, currency));
  setText(root, 'returns-single-event', formatMoney(r.singleReturnEvent, locale, currency));
  setText(root, 'low-inventory-fee', formatMoney(r.lowInventoryLevelFee, locale, currency));
  setText(
    root,
    'aged-surcharge-range',
    `${formatMoney(r.agedInventoryMin, locale, currency)} - ${formatMoney(r.agedInventoryMax, locale, currency)}`
  );

  const variableRow = findElementById<HTMLElement>(root, 'variable-closing-fee-row');
  setHidden(variableRow, r.variableClosingFee <= 0);

  const results = findElementById<HTMLElement>(root, 'results');
  setHidden(results, false);

  setText(
    root,
    'results-live-region',
    `Results updated. Profit per unit ${formatMoney(r.profitPerUnit, locale, currency)}, margin ${formatPercent(r.marginPct)}, ROI ${formatPercent(r.roiPct)}.`
  );

  applyRating(root, 'profit', r.profitPerUnit, 'profit-per-unit', 'profit-card');
  applyRating(root, 'margin', r.marginPct, 'margin', 'margin-card');
  applyRating(root, 'roi', r.roiPct, 'roi', 'roi-card');
}
